import { upperCase } from 'lodash';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@config/axios/axios.config';

import { paths } from '@common/types';
import axios from 'axios';
import { stableKey } from '@common/cache/util.ts';

interface IPracticeListProps {
  search: string;
  page: number;
  limit: number;
  sort_order?: string;
  sort_by?: string;
  partner_id?: string;
  state?: string;
}

const practiceListRequest = async (props: IPracticeListProps) => {
  const response = axiosInstance.get<
    paths[`/record/practices`]['get']['responses']['200']['content']['application/json']
  >(`/record/practices`, {
    params: {
      page: props.page,
      limit: props.limit,
      ...(props.search !== '' && { search: props.search }),
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.state !== '' && { state: upperCase(props.state) }),
      ...(props.partner_id !== '' && { partner_id: props.partner_id }),
    },
  });
  const data = (await response).data;

  return data;
};

export const usePracticeListQuery = (props: IPracticeListProps) => {
  const query = useQuery({
    queryKey: ['practices', stableKey(props)],
    queryFn: () => practiceListRequest(props),
    placeholderData: keepPreviousData,
  });

  return query;
};

interface IPracticeProps {
  practice_id: string;
}

const practiceRequest = async (props: IPracticeProps) => {
  const response = axiosInstance.get<
    paths[`/practice/{record_id}`]['get']['responses']['200']['content']['application/json']
  >(`/practice/${props.practice_id}`);
  const data = (await response).data;

  return data;
};

export const usePracticeQuery = (props: IPracticeProps) => {
  const query = useQuery({
    queryKey: ['practice', props.practice_id],
    queryFn: () => practiceRequest(props),
  });

  return query;
};

interface IPracticeHistoryProps {
  page: number;
  limit: number;
  practice_id: string;
  enabled: boolean;
}

const practiceHistoryRequest = async (props: IPracticeHistoryProps) => {
  const response = axiosInstance.get<
    paths[`/practice/{record_id}/history`]['get']['responses']['200']['content']['application/json']
  >(`/practice/${props.practice_id}/history`, {
    params: {
      page: props.page,
      limit: props.limit,
    },
  });
  const data = (await response).data;

  return data;
};

export const usePracticeHistoryQuery = ({
  practice_id,
  ...props
}: IPracticeHistoryProps) => {
  const query = useQuery({
    enabled: !!props.enabled,
    queryKey: ['practice', practice_id, 'history', stableKey(props)],
    queryFn: () => practiceHistoryRequest({ practice_id, ...props }),
  });

  return query;
};

interface IUsePracticeShortQueryProps {
  search?: string;
  record_id?: string;
}

const practiceShortRequest = async (props: IUsePracticeShortQueryProps) => {
  const response = axiosInstance.get<
    paths[`/record/practices/short`]['get']['responses']['200']['content']['application/json']
  >(`/record/practices/short`, {
    params: {
      search: props.search,
      ...(props.record_id && { record_id: props.record_id }),
    },
  });
  const data = (await response).data;
  return data;
};

export const usePracticeShortQuery = (props: IUsePracticeShortQueryProps) => {
  const orgQuery = useQuery({
    queryKey: ['practiceShort', props.record_id, props.search],
    queryFn: () => practiceShortRequest(props),
  });

  return orgQuery;
};

interface IPracticeLogoProps {
  practice_id: string;
}

const practiceLogoRequest = async (
  props: IPracticeLogoProps,
  signal?: AbortSignal,
): Promise<Blob | null> => {
  try {
    const response = await axiosInstance.get<Blob>(
      `/practice/${props.practice_id}/logo.webp`,
      {
        headers: { 'Content-type': 'image/webp' },
        responseType: 'blob',
        signal,
        skipNotFoundRedirect: true,
      },
    );
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
};
export const usePracticeLogoQuery = (props: IPracticeLogoProps) => {
  const query = useQuery({
    queryKey: ['practice', props.practice_id, 'logo'],
    queryFn: () => practiceLogoRequest(props),
  });

  return query;
};
